import HtmlInspectorSelect from './HtmlInspectorSelect';
import { useMemo, useState } from 'react';
import { buttonTypeOptions } from './PageBuilderConstants';
import { usePageBuilder } from './PageBuilderContext';
import { getHtmlCtas, updateHtmlCta } from './htmlCtas';
import styles from './BuilderInspector.module.css';
import BuilderInspectorHeader from './BuilderInspectorHeader';

export default function HtmlCtaEditor({ block }) {
    const { selectedHtmlCta, selectBlock, updateBlock } = usePageBuilder();
    const html = block.data?.html || '';
    const ctas = useMemo(() => getHtmlCtas(html), [html]);
    const [error, setError] = useState(null);
    const [attributeName, setAttributeName] = useState('');
    const index = selectedHtmlCta?.blockId === block.id ? selectedHtmlCta.index : 0;
    const cta = ctas[index] || ctas[0];
    if (!cta) return null;

    const update = change => {
        try {
            const nextHtml = updateHtmlCta(html, cta.index, change);
            updateBlock(block.id, { data: { ...block.data, html: nextHtml } });
            setError(null);
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        }
    };
    const attribute = (name, value) => update({ attribute: { name, value } });
    const customAttributes = Object.entries(cta.attributes).filter(([name]) =>
        /^(data-|aria-)/.test(name) && !['data-block-type', 'data-button-type', 'data-url', 'aria-label'].includes(name));

    return <section className={styles.editor} aria-label="CTA nell’HTML">
        <BuilderInspectorHeader type="cta" title="CTA nell’HTML" description="Configura azione, link e accessibilità." badge="CTA" />
        <label>CTA
            <HtmlInspectorSelect value={cta.index}
                options={ctas.map(item => ({ id: item.index, value: item.index, name: `${item.index + 1}. ${item.label.slice(0, 80)}` }))}
                onChange={value => { selectBlock(block.id, { htmlCtaIndex: value }); setError(null); }} />
        </label>
        {(cta.error || error) && <p role="alert">{cta.error || error}</p>}
        <fieldset disabled={Boolean(cta.error)}>
            <label>Azione
                <HtmlInspectorSelect value={cta.action} disabled={Boolean(cta.error)}
                    options={buttonTypeOptions.some(option => option.value === cta.action) ? buttonTypeOptions : [{ id: cta.action, value: cta.action, name: cta.action }, ...buttonTypeOptions]}
                    onChange={value => update({ action: value })} />
            </label>
            {cta.action === 'navigate_to' && <label>Link
                <input value={cta.url} onChange={event => update({ url: event.target.value })} />
            </label>}
            <label>Testo accessibilità
                <input value={cta.attributes['aria-label'] || ''} onChange={event => attribute('aria-label', event.target.value)} />
            </label>
            <label>Titolo
                <input value={cta.attributes.title || ''} onChange={event => attribute('title', event.target.value)} />
            </label>
            <strong className={styles.sectionTitle}>Attributi personalizzati</strong>
            {customAttributes.map(([name, value]) => <div className={styles.attribute} key={name}>
                <label>{name}<input value={value} onChange={event => attribute(name, event.target.value)} /></label>
                <button type="button" className={styles.danger} aria-label={`Rimuovi ${name}`} onClick={() => attribute(name, null)}>×</button>
            </div>)}
            <div className={styles.attribute}>
                <label>Nuovo attributo
                    <input placeholder="data-campaign" value={attributeName} onChange={event => setAttributeName(event.target.value)} />
                </label>
                <button type="button" disabled={!attributeName.trim()} onClick={() => {
                    const name = attributeName.trim().toLowerCase();
                    if (attribute(name, cta.attributes[name] ?? '')) setAttributeName('');
                }}>Aggiungi</button>
            </div>
            {['target', 'rel'].filter(name => name in cta.attributes).map(name => <label key={name}>{name}
                <input value={cta.attributes[name]} onChange={event => attribute(name, event.target.value)} />
            </label>)}
        </fieldset>
    </section>;
}
